import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import CTASection from '../components/CTASection';
import { useLanguage } from '../contexts/LanguageContext';
import { getSEOLanguage } from '../utils/seoHelpers';
import articlesData from '../data/articles.json';

const ArticleDetail = () => {
    const { slug } = useParams();
    const { t, language } = useLanguage();
    const navigate = useNavigate();

    // Determinar el idioma SEO según la lógica
    const seoLanguage = getSEOLanguage(language);

    // Find the article by slug
    const article = articlesData.find(a => a.slug === slug);

    useEffect(() => {
        // Scroll to top when article loads
        window.scrollTo(0, 0);
    }, [slug]);

    // If article not found, redirect to blog
    if (!article) {
        navigate(`/${language}/blog`);
        return null;
    }

    // Format content with line breaks
    const formatContent = (content) => {
        return content
            .split('\n')
            .filter(line => line.trim() !== '')
            .map((paragraph, index) => {
                const trimmed = paragraph.trim();

                if (trimmed.startsWith('##')) {
                    return (
                        <h4 key={index} className="article-heading">
                            {trimmed.replace('##', '').trim()}
                        </h4>
                    );
                }

                if (trimmed.startsWith('-')) {
                    return (
                        <li key={index} className="article-list-item">
                            {trimmed.replace('-', '').trim()}
                        </li>
                    );
                }

                if (trimmed.match(/^\d+\./)) {
                    return (
                        <li key={index} className="article-list-item">
                            {trimmed.replace(/^\d+\./, '').trim()}
                        </li>
                    );
                }

                return (
                    <p key={index} className="article-paragraph">
                        {paragraph}
                    </p>
                );
            });
    };

    // Get related articles (same category, excluding current)
    const relatedArticles = articlesData
        .filter(a => a.id !== article.id && a.category[language] === article.category[language])
        .slice(0, 3);

    return (
        <>
            <Helmet>
                <html lang={seoLanguage} />
                <title>{article.seo.title[seoLanguage]}</title>
                <meta name="description" content={article.seo.description[seoLanguage]} />
                <meta name="keywords" content={article.seo.keywords[seoLanguage]} />
                <meta property="og:title" content={article.seo.title[seoLanguage]} />
                <meta property="og:description" content={article.seo.description[seoLanguage]} />
                <meta property="og:image" content={article.image} />
                <meta property="og:type" content="article" />
                <meta property="og:locale" content={seoLanguage === 'ca' ? 'ca_ES' : 'es_ES'} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={article.seo.title[seoLanguage]} />
                <meta name="twitter:description" content={article.seo.description[seoLanguage]} />
                <meta name="twitter:image" content={article.image} />
            </Helmet>

            {/* Section - Blog Article Start */}
            <div className="mxd-section padding-pre-title" style={{ paddingTop: '4rem' }}>
                <div className="mxd-container grid-container">
                    <div className="mxd-article-area loading-wrap">
                        {/* Article Container Start */}
                        <div className="mxd-article-container mxd-grid-item no-margin">
                            {/* Article Start */}
                            <article className="mxd-article">
                                {/* Article Headline Start */}
                                <div className="mxd-article__headline">
                                    <div className="mxd-article__meta">
                                        <div className="mxd-article__data loading__item">
                                            <span className="meta-date">
                                                {new Date(article.publishDate).toLocaleDateString(language, {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                                <svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 20 20">
                                                    <path d="M19.6,9.6h-3.9c-.4,0-1.8-.2-1.8-.2-.6,0-1.1-.2-1.6-.6-.5-.3-.9-.8-1.2-1.2-.3-.4-.4-.9-.5-1.4,0,0,0-1.1-.2-1.5V.4c0-.2-.2-.4-.4-.4s-.4.2-.4.4v4.4c0,.4-.2,1.5-.2,1.5,0,.5-.2,1-.5,1.4-.3.5-.7.9-1.2,1.2s-1,.5-1.6.6c0,0-1.2,0-1.7.2H.4c-.2,0-.4.2-.4.4s.2.4.4.4h4.1c.4,0,1.7.2,1.7.2.6,0,1.1.2,1.6.6.4.3.8.7,1.1,1.1.3.5.5,1,.6,1.6,0,0,0,1.3.2,1.7v4.1c0,.2.2.4.4.4s.4-.2.4-.4v-4.1c0-.4.2-1.7.2-1.7,0-.6.2-1.1.6-1.6.3-.4.7-.8,1.1-1.1.5-.3,1-.5,1.6-.6,0,0,1.3,0,1.8-.2h3.9c.2,0,.4-.2.4-.4s-.2-.4-.4-.4h0Z" />
                                                </svg>
                                            </span>
                                            <span className="meta-time">{article.readTime[language]}</span>
                                        </div>
                                    </div>
                                    <div className="mxd-article__title loading__item">
                                        <h1 className="h1-small">{article.title[language]}</h1>
                                    </div>
                                </div>
                                {/* Article Headline End */}

                                {/* Article Content Start */}
                                <div className="mxd-article__content">
                                    <div className="mxd-article__block">
                                        <p className="t-large mxd-article__excerpt">{article.excerpt[language]}</p>
                                    </div>
                                    <div className="mxd-article__block">
                                        {article.id === '7' && (
                                            <div className="mxd-article__block" style={{ marginBottom: '2rem' }}>
                                                <p className="article-paragraph">
                                                    {t('blog.article.whatsapp_cta.text') || 'Si quieres un presupuesto personalizado para tu comunidad, puedes contactarnos directamente por WhatsApp y te orientamos sin compromiso.'}
                                                </p>
                                                <a
                                                    href="https://wa.me/34610683948"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-anim btn-default btn-outline slide-right-up"
                                                >
                                                    <span className="btn-caption">
                                                        {t('blog.article.whatsapp_cta.button') || 'Escríbenos por WhatsApp'}
                                                    </span>
                                                    <i className="ph-bold ph-whatsapp-logo"></i>
                                                </a>
                                            </div>
                                        )}
                                        {formatContent(article.content[language])}
                                    </div>
                                </div>
                                {/* Article Content End */}
                            </article>
                            {/* Article End */}

                            {/* Article Author Start */}
                            {article.author && (
                                <div className="mxd-article-author">
                                    <div className="mxd-article-author__data">
                                        <div className="mxd-article-author__avatar">
                                            {article.author.avatar ? (
                                                <img
                                                    src={article.author.avatar}
                                                    alt={article.author.name}
                                                />
                                            ) : (
                                                <div style={{
                                                    width: '80px',
                                                    height: '80px',
                                                    borderRadius: '50%',
                                                    background: 'var(--base-opp)',
                                                    color: 'var(--base)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '2rem',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {article.author.name.charAt(0)}
                                                </div>
                                            )}
                                        </div>
                                        <div className="mxd-article-author__info">
                                            <h5 className="mxd-article-author__name">
                                                {article.author.name}
                                                <small className="mxd-article-author__position">{article.author.role[language]}</small>
                                            </h5>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {/* Article Author End */}
                        </div>
                        {/* Article Container End */}
                    </div>
                </div>
            </div>
            {/* Section - Blog Article End */}

            {/* Section - More on Topic Start */}
            {relatedArticles.length > 0 && (
                <div className="mxd-section padding-blog">
                    <div className="mxd-container grid-container">
                        {/* Block - Section Title Start */}
                        <div className="mxd-block">
                            <div className="mxd-section-title pre-grid">
                                <div className="container-fluid p-0">
                                    <div className="row g-0">
                                        <div className="col-12 col-xl-5 mxd-grid-item no-margin">
                                            <div className="mxd-section-title__hrtitle">
                                                <h2 className="reveal-type anim-uni-in-up">{t('blog.article.related_articles') || 'Artículos relacionados'}</h2>
                                            </div>
                                        </div>
                                        <div className="col-12 col-xl-4 mxd-grid-item no-margin"></div>
                                        <div className="col-12 col-xl-3 mxd-grid-item no-margin">
                                            <div className="mxd-section-title__hrcontrols anim-uni-in-up">
                                                <Link className="btn btn-anim btn-default btn-outline slide-right-up" to={`/${language}/blog`}>
                                                    <span className="btn-caption">{t('blog.all_articles') || 'Todos los artículos'}</span>
                                                    <i className="ph-bold ph-arrow-up-right"></i>
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Block - Section Title End */}

                        {/* Block - Blog Preview Cards Start */}
                        <div className="mxd-block">
                            <div className="mxd-blog-preview">
                                <div className="container-fluid p-0">
                                    <div className="row g-0">
                                        {relatedArticles.map((relatedArticle, index) => (
                                            <div key={relatedArticle.id} className="col-12 col-xl-4 mxd-blog-preview__item mxd-grid-item animate-card-3">
                                                <Link className="mxd-blog-preview__media" to={`/${language}/blog/${relatedArticle.slug}`}>
                                                    <div className="mxd-blog-preview__image parallax-img-small" style={{
                                                        backgroundImage: `url(${relatedArticle.image})`,
                                                        backgroundSize: 'cover',
                                                        backgroundPosition: 'center',
                                                        paddingBottom: '125%'
                                                    }}>
                                                    </div>
                                                    <div className="mxd-preview-hover">
                                                        <i className="mxd-preview-hover__icon">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56" fill="none">
                                                                <path d="M28 42C35.732 42 42 35.732 42 28C42 20.268 35.732 14 28 14C20.268 14 14 20.268 14 28C14 35.732 20.268 42 28 42Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                <path d="M28 32.6667C30.5773 32.6667 32.6667 30.5773 32.6667 28C32.6667 25.4227 30.5773 23.3333 28 23.3333C25.4227 23.3333 23.3333 25.4227 23.3333 28C23.3333 30.5773 25.4227 32.6667 28 32.6667Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>
                                                        </i>
                                                    </div>
                                                    <div className="mxd-blog-preview__tags">
                                                        <span className="tag tag-default tag-permanent">{relatedArticle.category[language]}</span>
                                                    </div>
                                                </Link>
                                                <div className="mxd-blog-preview__data">
                                                    <Link className="anim-uni-in-up" to={`/${language}/blog/${relatedArticle.slug}`}>
                                                        <span>{relatedArticle.title[language]}</span>
                                                    </Link>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Block - Blog Preview Cards End */}
                    </div>
                </div>
            )}
            {/* Section - More on Topic End */}

            {/* CTA Section Start */}
            <CTASection
                title={t('blog.article.cta_title')}
                description={t('blog.article.cta_description')}
                buttonText={t('blog.article.cta_button')}
                icon="ph-bold ph-chat-circle-dots"
                iconType="icon"
            />
            {/* CTA Section End */}
        </>
    );
};

export default ArticleDetail;